import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/de';

import { AuthProvider, useAuth } from './services/AuthContext';
import { DymoProvider } from './contexts/DymoContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import CertificatesPage from './pages/CertificatesPage';
import CertificateDetailPage from './pages/CertificateDetailPage';
import ChargesPage from './pages/ChargesPage';
import MasterDataPage from './pages/MasterDataPage';
import UsersPage from './pages/UsersPage';
import DymoTestPage from './pages/DymoTestPage';
import ProductionListPage from './pages/ProductionListPage';
import ProductionScanPage from './pages/ProductionScanPage';
import LoadingSpinner from './components/LoadingSpinner';
import { ToastProvider } from './components/ToastProvider';
import theme from './theme';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireKalotte?: boolean;
  requireProduction?: boolean;
  hideFromOperator?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireKalotte = false,
  requireProduction = false,
  hideFromOperator = false,
}) => {
  const { isAuthenticated, isKalotteUser, canAccessProduction, isOperator, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (hideFromOperator && isOperator) {
    return <Navigate to="/production" replace />;
  }

  if (requireKalotte && !isKalotteUser) {
    return <Navigate to="/" replace />;
  }

  if (requireProduction && !canAccessProduction) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const HomeRedirect: React.FC = () => {
  const { isOperator } = useAuth();
  return <Navigate to={isOperator ? '/production' : '/certificates'} replace />;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<HomeRedirect />} />
        <Route
          path="/certificates"
          element={
            <ProtectedRoute hideFromOperator>
              <CertificatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/certificates/:id"
          element={
            <ProtectedRoute hideFromOperator>
              <CertificateDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/charges"
          element={
            <ProtectedRoute hideFromOperator>
              <ChargesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/production"
          element={
            <ProtectedRoute requireProduction>
              <ProductionListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/production/:id"
          element={
            <ProtectedRoute requireProduction>
              <ProductionScanPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dymo-test"
          element={
            <ProtectedRoute requireKalotte>
              <DymoTestPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/master-data"
          element={
            <ProtectedRoute requireKalotte>
              <MasterDataPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute requireKalotte>
              <UsersPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<HomeRedirect />} />
      </Routes>
    </Layout>
  );
};

const App: React.FC = () => {
  return (
    <DymoProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
          <ToastProvider>
            <Router>
              <AuthProvider>
                <AppRoutes />
              </AuthProvider>
            </Router>
          </ToastProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </DymoProvider>
  );
};

export default App;
